import { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

// Crea il context
export const ColorModeContext = createContext({ 
  toggleColorMode: () => {},
  setMode: () => {},
  mode: 'dark' 
});

// Provider del tema
export const ColorModeContextProvider = ({ children }) => {
  // Controlla se c'è una preferenza salvata nel localStorage
  const storedMode = localStorage.getItem('themeMode') || 'dark';
  const [mode, setMode] = useState(storedMode);
  
  // Salva la preferenza nel localStorage quando cambia
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);
  
  // Funzione per invertire il tema
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      setMode: (newMode) => {
        setMode(newMode);
      },
      mode,
    }),
    [mode],
  );
  
  // Definizione dei temi disponibili
  const theme = useMemo(
    () => {
      if (mode === 'dark') {
        // Tema scuro - migliorato
        return createTheme({
          palette: {
            mode: 'dark',
            primary: {
              main: '#90caf9',
              light: '#a6d4fa',
              dark: '#648dae',
              contrastText: '#000000',
            },
            secondary: {
              main: '#f48fb1',
              light: '#f6a5c0',
              dark: '#aa647b',
              contrastText: '#000000',
            },
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
            text: {
              primary: '#ffffff',
              secondary: 'rgba(255, 255, 255, 0.7)',
            },
            divider: 'rgba(255, 255, 255, 0.12)',
            action: {
              hover: 'rgba(255, 255, 255, 0.08)',
              selected: 'rgba(255, 255, 255, 0.12)',
              disabled: 'rgba(255, 255, 255, 0.3)',
            },
            error: {
              main: '#f44336',
              light: '#e57373',
              dark: '#d32f2f',
            },
            warning: {
              main: '#ff9800',
              light: '#ffb74d',
              dark: '#f57c00',
            },
            info: {
              main: '#2196f3',
              light: '#64b5f6',
              dark: '#1976d2',
            },
            success: {
              main: '#4caf50',
              light: '#81c784',
              dark: '#388e3c',
            },
          },
        });
      } else if (mode === 'brown') {
        // Tema marrone - migliorato
        return createTheme({
          palette: {
            mode: 'light',
            primary: {
              main: '#d2a679',
              light: '#e4c29f',
              dark: '#a0845e',
              contrastText: '#ffffff',
            },
            secondary: {
              main: '#8d6e63',
              light: '#a1887f',
              dark: '#5d4037',
              contrastText: '#ffffff',
            },
            background: {
              default: '#f5f0e8',
              paper: '#eee0cb',
            },
            text: {
              primary: '#5d4037',
              secondary: '#8d6e63',
            },
            divider: '#a1887f',
            action: {
              hover: '#c8b393',
              selected: '#b39c7d',
              disabled: '#bdbdbd',
            },
            error: {
              main: '#d32f2f',
              light: '#ef5350',
              dark: '#c62828',
            },
            warning: {
              main: '#f57c00',
              light: '#ff9800',
              dark: '#e65100',
            },
            info: {
              main: '#1976d2',
              light: '#2196f3',
              dark: '#0d47a1',
            },
            success: {
              main: '#388e3c',
              light: '#4caf50',
              dark: '#2e7d32',
            },
          },
          shadows: [
            'none',
            '0px 2px 1px -1px rgba(93,64,55,0.2),0px 1px 1px 0px rgba(93,64,55,0.14),0px 1px 3px 0px rgba(93,64,55,0.12)',
            '0px 3px 1px -2px rgba(93,64,55,0.2),0px 2px 2px 0px rgba(93,64,55,0.14),0px 1px 5px 0px rgba(93,64,55,0.12)',
            '0px 3px 3px -2px rgba(93,64,55,0.2),0px 3px 4px 0px rgba(93,64,55,0.14),0px 1px 8px 0px rgba(93,64,55,0.12)',
            '0px 2px 4px -1px rgba(93,64,55,0.2),0px 4px 5px 0px rgba(93,64,55,0.14),0px 1px 10px 0px rgba(93,64,55,0.12)',
            // Aggiungi più shadow se necessario...
            ...Array(20).fill('0px 2px 4px -1px rgba(93,64,55,0.2),0px 4px 5px 0px rgba(93,64,55,0.14),0px 1px 10px 0px rgba(93,64,55,0.12)')
          ],
        });
      } else {
        // Tema chiaro - migliorato
        return createTheme({
          palette: {
            mode: 'light',
            primary: {
              main: '#1976d2',
              light: '#42a5f5',
              dark: '#1565c0',
              contrastText: '#ffffff',
            },
            secondary: {
              main: '#e91e63',
              light: '#f06292',
              dark: '#c2185b',
              contrastText: '#ffffff',
            },
            background: {
              default: '#f5f5f5',
              paper: '#ffffff',
            },
            text: {
              primary: 'rgba(0, 0, 0, 0.87)',
              secondary: 'rgba(0, 0, 0, 0.6)',
            },
            divider: 'rgba(0, 0, 0, 0.12)',
            action: {
              hover: 'rgba(0, 0, 0, 0.04)',
              selected: 'rgba(0, 0, 0, 0.08)',
              disabled: 'rgba(0, 0, 0, 0.26)',
            },
            error: {
              main: '#d32f2f',
              light: '#ef5350',
              dark: '#c62828',
            },
            warning: {
              main: '#ed6c02',
              light: '#ff9800',
              dark: '#e65100',
            },
            info: {
              main: '#0288d1',
              light: '#03a9f4',
              dark: '#01579b',
            },
            success: {
              main: '#2e7d32',
              light: '#4caf50',
              dark: '#1b5e20',
            },
          },
        });
      }
    },
    [mode],
  );
  
  return (
    <ColorModeContext.Provider value={colorMode}>
      {children(mode, colorMode.setMode, theme)}
    </ColorModeContext.Provider>
  );
};

